import {
  createActionsColumn,
  TableCellProps,
  WbButton,
  WbDrawer,
  WbDrawerContent,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { Entity } from '@backstage/catalog-model';
import { Box, Switch, Typography, makeStyles } from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import { FC, useEffect, useMemo, useState } from 'react';
import { useAssignmentsContext } from './AssignmentsContext';

const useStyles = makeStyles(
  theme => ({
    flexBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  }),
  { name: 'EditEnabledScopesDrawer' },
);

// helper for creating a valid id from the urn (replaces unsafe characters with '-')
const makeScopeLabelId = (urn?: string) =>
  urn
    ? `scope-label-${encodeURIComponent(urn).replace(/[%]/g, '-')}`
    : undefined;

export const EditEnabledScopesDrawer: FC = () => {
  const classes = useStyles();
  const {
    openEditEnabledScopesDrawer,
    setEditEnabledScopesDrawer,
    selectedRow,
    setSelectedRow,
    updateAssignments,
  } = useAssignmentsContext();

  // clone the elements to avoid modifying the original ones
  const initialScopes = useMemo(
    () => selectedRow?.scopes?.map(s => ({ ...s })) ?? [],
    [selectedRow],
  );

  const [scopes, setScopes] =
    useState<{ urn: string; entity?: Entity; enabled?: boolean }[]>(
      initialScopes,
    );

  // Initialize scopes only when opening the drawer to avoid reset while editing
  useEffect(() => {
    if (openEditEnabledScopesDrawer) {
      setScopes(initialScopes);
    }
  }, [initialScopes, openEditEnabledScopesDrawer]);

  const columns: TableCellProps<{
    urn: string;
    entity?: Entity;
    enabled?: boolean;
  }>[] = useMemo(() => {
    return [
      {
        field: 'urn',
        cellProps: {
          size: 'small',
          align: 'left',
          style: { wordBreak: 'break-word' },
        },
        headerName: 'scope',
        fieldRender: field => {
          const labelId = makeScopeLabelId(field.urn);
          const display =
            field.entity?.metadata.displayName ??
            (field.entity?.spec?.mesh as any)?.name ??
            field.entity?.metadata.name ??
            field?.urn;
          return (
            <Typography variant="body2" id={labelId}>
              {display}
            </Typography>
          );
        },
      },
      {
        field: 'entity',
        cellProps: {
          size: 'small',
          align: 'left',
          style: { wordBreak: 'break-word' },
        },
        headerName: 'type',
        fieldRender: field => (
          <Typography variant="body2">{field.entity?.kind ?? '-'}</Typography>
        ),
      },
      createActionsColumn({
        width: 80,
        renderActions: field => {
          const labelId = makeScopeLabelId(field.urn);
          const display =
            field.entity?.metadata.displayName ??
            (field.entity?.spec?.mesh as any)?.name ??
            field.entity?.metadata.name ??
            field?.urn;
          return (
            <Switch
              disabled={field?.urn === '*'}
              checked={Boolean(field.enabled)}
              // block bubbling on click to prevent the table from receiving the event
              onClick={event => {
                event.stopPropagation();
              }}
              onChange={(_event, checked) => {
                setScopes(prevScopes =>
                  prevScopes.map(scope =>
                    scope.urn === field.urn
                      ? { ...scope, enabled: checked }
                      : scope,
                  ),
                );
              }}
              inputProps={
                labelId
                  ? { 'aria-labelledby': labelId }
                  : { 'aria-label': `Enable ${display}` }
              }
            />
          );
        },
      }),
    ];
  }, []);

  return (
    <WbDrawer
      key={selectedRow?.userOrGroup?.urn}
      open={openEditEnabledScopesDrawer}
      onClose={() => setSelectedRow(undefined)}
      setOpen={setEditEnabledScopesDrawer}
      title="Scope settings"
      size={50}
      actions={
        <>
          <WbButton
            onClick={() => {
              setEditEnabledScopesDrawer(false);
              setSelectedRow(undefined);
            }}
            variant="outlined"
            color="primary"
            label="Cancel"
          />
          <WbButton
            disabled={selectedRow?.scopes?.[0]?.urn === '*'}
            onClick={() => {
              updateAssignments(
                selectedRow?.userOrGroup?.urn!,
                scopes?.map(s => ({
                  enabled: s.enabled ?? true,
                  urn: s?.urn,
                }))!,
              );
            }}
            label="Update"
            variant="contained"
            color="primary"
          />
        </>
      }
    >
      <WbDrawerContent style={{ overflow: 'hidden' }}>
        <Box className={classes.flexBox} marginBottom="8px">
          {selectedRow?.userOrGroup.entity ? (
            <>
              {selectedRow?.userOrGroup?.entity.kind === 'User' ? (
                <PersonIcon fontSize="small" />
              ) : (
                <GroupIcon fontSize="small" />
              )}
              <Typography variant="body1" style={{ fontWeight: '500' }}>
                {(selectedRow?.userOrGroup?.entity?.spec?.profile as any)
                  ?.displayName ??
                  selectedRow?.userOrGroup?.entity?.metadata.name ??
                  selectedRow?.userOrGroup?.urn}
              </Typography>
            </>
          ) : (
            <Typography variant="body1" style={{ fontWeight: '500' }}>
              {selectedRow?.userOrGroup?.urn}
            </Typography>
          )}
        </Box>

        <WbTable<{ urn: string; entity?: Entity; enabled?: boolean }>
          styles={{
            container: { height: '100%', flex: '1 1 0' },
          }}
          components={{
            tableContent: {
              columns,
              rows: scopes,
            },
          }}
        />
      </WbDrawerContent>
    </WbDrawer>
  );
};
